import React from 'react'
import moment from 'moment'
import injectStyles from 'react-jss'
import SimpleTableTable from '../../../../Components/SimpleTableTable'
import StaticGMap from '../../../../Components/StaticGMap/StaticGMap'
import images from '../../../../Assets/Images'
import { numberWithCommas } from './../../../../Utils/Formatters/index'

const convertToMonetaryValue = (value, currencySign) => {
  const parsedValue = Math.floor(value) / 100
  return `${currencySign}${numberWithCommas(parsedValue)}`
}

const CampaignDetails = props => {
  const {
    printingCostPerFlyer = 0,
    distributionCost = 0,
    bonusCost = 0,
    cities,
    currencySign = '',
    //quoteURL,
    classes,
    campaignName,
  } = props

  const totalDeliveryPoints = cities.reduce(
    // NOTE: Because we round down pack sizes now we can't quote we will
    // deliver to more houses than we have flyers for so switch this to
    // total totalFlyers
    //(acc, cityData) => acc + cityData.householdsAmount,
    (acc, cityData) => acc + cityData.totalFlyers,
    0,
  )
  const totalFlyers = cities.reduce(
    (acc, cityData) => acc + cityData.totalFlyers,
    0,
  )
  const totalMissionsAmount = cities.reduce(
    (acc, cityData) => acc + cityData.missionsAmount,
    0,
  )
  const totalPrintingCost = totalFlyers * printingCostPerFlyer
  const totalDistributionCost = totalMissionsAmount * distributionCost

  const totalInfoTableData = [
    {
      'Total number of missions': totalMissionsAmount,
      'Total distribution cost': totalDistributionCost,
      'Total number of flyers': totalDeliveryPoints,
      'Total printing cost': totalPrintingCost,
      'Total bonus cost': bonusCost,
      'Total cost (excl. tax)':
        totalPrintingCost + totalDistributionCost + (bonusCost || 0),
    },
  ]

  const postcodes = cities.reduce((acc, cityData) => {
    const cityPostcodes = Object.entries(cityData.postcodes).map(
      ([name, { householdsAmount, missionsAmount, totalFlyers }]) => {
        return {
          name: name === 'Unknown postcode' ? `N/A` : name,
          cityName: cityData.city.name,
          householdsAmount,
          totalFlyers,
          missionsAmount,
        }
      },
    )
    acc.push(...cityPostcodes)
    return acc
  }, [])

  const shouldPlacePostcodesOnNextPage = cities.length + postcodes.length > 10

  const renderHeader = () => (
    <div className={classes.topContainer}>
      <div>
        <h1 className={classes.header}>
          Campaign summary{' '}
          {typeof campaignName === 'string' && campaignName.length > 0
            ? `for "${campaignName}"`
            : ''}
        </h1>
        <span>Generated at:</span>
        <span>{moment().format('DD/M/YYYY')}</span>
      </div>
      <img
        className={classes.topContainerLogo}
        src={images.mainLogo}
        alt="Logo"
      />
    </div>
  )

  const renderPageBreak = () => (
    <React.Fragment>
      <div className="html2pdf__page-break" />
      {renderHeader()}
    </React.Fragment>
  )

  return (
    <React.Fragment>
      {renderHeader()}
      <SimpleTableTable
        header={[
          'Total number of missions',
          'Total number of flyers',
          'Total printing cost',
          'Total distribution cost',
          'Total bonus cost',
          'Total cost (excl. tax)',
        ]}
        data={totalInfoTableData}
        showPages={false}
        postRowRender={m => ({
          ...m,
          'Total number of flyers': numberWithCommas(
            m['Total number of flyers'],
          ),
          'Total number of missions': numberWithCommas(
            m['Total number of missions'],
          ),
          'Total distribution cost': convertToMonetaryValue(
            m['Total distribution cost'],
            currencySign,
          ),
          'Total printing cost': convertToMonetaryValue(
            m['Total printing cost'],
            currencySign,
          ),
          'Total bonus cost': convertToMonetaryValue(
            m['Total bonus cost'],
            currencySign,
          ),
          'Total cost (excl. tax)': convertToMonetaryValue(
            m['Total cost (excl. tax)'],
            currencySign,
          ),
        })}
      />
      {cities.length > 1 && (
        <div className={classes.container}>
          <h1 className={classes.header}>By city</h1>
          <SimpleTableTable
            header={[
              'City',
              'Total number of missions',
              'Total number of flyers',
              'Total printing cost',
              'Total distribution cost',
              'Total cost (excl. tax)',
            ]}
            data={cities.map(
              ({ city, householdsAmount, totalFlyers, missionsAmount }) => ({
                'City': city.name,
                'Total number of flyers': totalFlyers,
                'Total number of missions': missionsAmount,
                'Total distribution cost': missionsAmount * distributionCost,
                'Total printing cost': totalFlyers * printingCostPerFlyer,
                'Total cost (excl. tax)':
                  missionsAmount * distributionCost +
                  totalFlyers * printingCostPerFlyer,
              }),
            )}
            showPages={false}
            postRowRender={m => ({
              ...m,
              'Total number of flyers': numberWithCommas(
                m['Total number of flyers'],
              ),
              'Total number of missions': numberWithCommas(
                m['Total number of missions'],
              ),
              'Total distribution cost': convertToMonetaryValue(
                m['Total distribution cost'],
                currencySign,
              ),
              'Total printing cost': convertToMonetaryValue(
                m['Total printing cost'],
                currencySign,
              ),
              'Total cost (excl. tax)': convertToMonetaryValue(
                m['Total cost (excl. tax)'],
                currencySign,
              ),
            })}
          />
        </div>
      )}

      {shouldPlacePostcodesOnNextPage && renderPageBreak()}
      {postcodes.length > 1 && (
        <div className={classes.container}>
          <h1 className={classes.header}>By postcode</h1>
          <SimpleTableTable
            header={[
              'City',
              'Postcode',
              'Total number of missions',
              'Total number of flyers',
              'Total printing cost',
              'Total distribution cost',
              'Total cost (excl. tax)',
            ]}
            data={postcodes.map(
              ({
                name,
                cityName,
                householdsAmount,
                totalFlyers,
                missionsAmount,
              }) => {
                return {
                  'City': cityName,
                  'Postcode': name,
                  // Switched to total flyers because we round down pack sizes
                  'Total number of flyers': totalFlyers,
                  'Total number of missions': missionsAmount,
                  'Total distribution cost': missionsAmount * distributionCost,
                  'Total printing cost': totalFlyers * printingCostPerFlyer,
                  'Total cost (excl. tax)':
                    missionsAmount * distributionCost +
                    totalFlyers * printingCostPerFlyer,
                }
              },
            )}
            postRowRender={m => ({
              ...m,
              'Total number of flyers': numberWithCommas(
                m['Total number of flyers'],
              ),
              'Total number of missions': numberWithCommas(
                m['Total number of missions'],
              ),
              'Total distribution cost': convertToMonetaryValue(
                m['Total distribution cost'],
                currencySign,
              ),
              'Total printing cost': convertToMonetaryValue(
                m['Total printing cost'],
                currencySign,
              ),
              'Total cost (excl. tax)': convertToMonetaryValue(
                m['Total cost (excl. tax)'],
                currencySign,
              ),
            })}
            showPages={false}
          />
        </div>
      )}

      {cities.map(cityData => {
        var overlays = cityData.overlays.slice(0)
        // After about 30 rendered maps it stops working, this is a crude
        // work around
        if (totalMissionsAmount > 30) {
          var warning = <p>NOTE: Only a sample of missions are shown below.</p>
          overlays = overlays.slice(0, Math.floor(30 / cities.length))
        }
        return (
          <React.Fragment>
            {renderPageBreak()}
            <div className={classes.container}>
              <h1 className={classes.header}>
                Missions in {cityData.city.name}
              </h1>
              {warning}
              <div className={classes.mapContainer}>
                {overlays.map((overlay, index) => (
                  <React.Fragment>
                    {/* Place 2 map images on 1 page */}
                    {index != 0 && index % 2 === 0 ? renderPageBreak() : null}
                    <div style={{ marginTop: 10, marginBottom: 10 }}>
                      <StaticGMap
                        width={350}
                        height={350}
                        zoom={null}
                        boundary={overlay.boundary}
                      />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </React.Fragment>
        )
      })}
      {/* PAD-4473 It was decided to hide this page as it's buggy and not very
          useful */}
      {/*{quoteURL ? (
        <React.Fragment>
          {renderPageBreak()}
          <h2>Internal use only</h2>
          <a href={quoteURL}>Click to build campaign</a>
          <br />
        </React.Fragment>
      ) : null}*/}
    </React.Fragment>
  )
}

const styles = {
  header: {
    fontSize: 28,
    marginTop: 24,
  },
  mapContainer: { marginTop: 24 },
  topContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  topContainerLogo: { height: 100 },
}

export default injectStyles(styles)(CampaignDetails)



// WEBPACK FOOTER //
// ./src/Containers/Form/Campaigns/CreateEditCampaign/CampaignDetails.js